import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, FileText, Truck } from "lucide-react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import Banner from "../../../components/ui/Banner/Banner.jsx";
import TrucksTable from "../../../components/tables/TrucksTable/TrucksTable.jsx";
import { MONTHS, WEIGHTS } from "../../../data/rates.js";
import { useApp } from "../../../hooks/useApp.js";
import { useTotals } from "../../../hooks/useTotals.js";
import { money, round2 } from "../../../utils/format.js";
import { truckTax } from "../../../utils/tax.js";
import { getToken } from "../../../services/api.js";
import { listTaxPreparerOptions } from "../../../services/taxPreparerService.js";
import { getPriorYear } from "../../../services/priorYearService.js";
import { getCredits } from "../../../services/creditsService.js";
import { getCreditReasons } from "../../../services/lookupService.js";
import { validateXml } from "../../../services/efileService.js";

const RETURN_TYPE_LABELS = {
  original: "Form2290",
  amended: "Amended return",
  vincorrection: "VIN correction",
  final: "Final return",
};

// Matches TrucksTable's/VinCorrectionStep's weightLabelFor - backend lookup
// rows ({ code, weightLabel }) take priority, falling back to the static
// WEIGHTS map.
const weightLabelFor = (cat, weightCategories) =>
  weightCategories.find((w) => w.code === cat)?.weightLabel ?? WEIGHTS[cat];

const AMEND_REASON_LABELS = {
  weight: "Taxable gross weight increased",
  mileage: "Suspended vehicle went over its mileage limit",
};

// Matches CreditsStep.jsx's fallback list - kept here too so credit reason
// codes still render as labels if the lookup fetch is slow or fails.
const DEFAULT_CREDIT_REASONS = [
  ["sold", "Sold, destroyed, or stolen before June 1"],
  ["mileage", "Used 5,000 miles or less (7,500 agricultural) last period"],
];

const ReviewStep = ({ onNext, onBack, viewOnly = false, onGoToStep, onEditBusiness, onEditPreparer }) => {
  const { state } = useApp();
  const t = useTotals();
  const navigate = useNavigate();
  const [preparerName, setPreparerName] = useState("");
  const [validating, setValidating] = useState(false);

  // Per-section "Edit" button, mirroring the Form 720 app's summary page -
  // jumps straight to whatever step (or standalone Business/Tax Preparer
  // page) owns that section's data, instead of forcing a click back through
  // every step in order. Hidden entirely in viewOnly mode (submitted/
  // accepted/rejected returns aren't editable - see Wizard.jsx).
  const EditButton = ({ onClick }) => {
    if (viewOnly || !onClick) return null;
    return (
      <Button variant="ghost" size="sm" className="!text-xs !px-2.5 !py-1 shrink-0" onClick={onClick}>
        ✎ Edit
      </Button>
    );
  };

  // Small rounded icon badge, matching the Dashboard's StatCard treatment -
  // used to give each of the three summary cards a quick visual anchor.
  const CardIcon = ({ icon: Icon }) => (
    <div className="w-8 h-8 rounded-[8px] bg-[var(--accent-soft)] text-[var(--accent)] grid place-items-center shrink-0 mb-2.5">
      <Icon size={16} />
    </div>
  );

  const isFinalReturn = !!state.finalReturn || state.returnType === "final";
  const isVinCorrection = state.returnType === "vincorrection";
  const isAmended = state.returnType === "amended";
  const isWeightAmended = isAmended && state.amendReason === "weight";
  const isMileageAmended = isAmended && state.amendReason === "mileage";
  const isOriginal = !isFinalReturn && !isVinCorrection && !isAmended;
  // A final return may still carry vehicles/tax (F2290-027-01 only requires
  // a VIN when the Final checkbox is NOT checked) - only show the
  // simplified "nothing to report" summary when it's truly empty.
  const isEmptyFinalReturn = isFinalReturn && !state.trucks.length;
  // Prior Year (Part II) and Credits (Line 5) are plain siblings of
  // FinalReturnInd in IRS2290.xsd - a Final filer gets the same two
  // sections here as an Original filer (see stepConfig.js).
  const showsPriorYear = isOriginal || isFinalReturn;

  const [priorYear, setPriorYear] = useState(null);
  const [credits, setCredits] = useState([]);
  const [creditReasons, setCreditReasons] = useState(DEFAULT_CREDIT_REASONS);

  // Credits (Line 5) are saved straight to the backend by CreditsStep and
  // never written into wizard state, so - like priorYear/business above -
  // Review has to fetch the saved rows itself.
  useEffect(() => {
    if (!getToken() || !state.returnId) return;
    let cancelled = false;
    getCredits(state.returnId)
      .then((saved) => {
        if (!cancelled) setCredits(saved);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [state.returnId]);

  useEffect(() => {
    let cancelled = false;
    getCreditReasons()
      .then((reasons) => {
        if (!cancelled && Array.isArray(reasons) && reasons.length) {
          setCreditReasons(reasons.map((r) => [r.code, r.label]));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // PriorYearStep saves straight to the backend (it's never written into
  // wizard state), so - like the business/preparer reloads above - Review
  // has to fetch it itself rather than reading it off state.
  useEffect(() => {
    if (!showsPriorYear || !getToken() || !state.returnId) return;
    let cancelled = false;
    getPriorYear(state.returnId)
      .then((saved) => {
        if (!cancelled) setPriorYear(saved);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showsPriorYear, state.returnId]);

  const hasPriorYearData =
    !!priorYear && (priorYear.line8aChecked || priorYear.line9Checked);

  // The full business record (name, EIN, taxPreparerId, ...) is fetched once,
  // for every step, by Wizard.jsx (keyed on state.businessId) - no need to
  // duplicate that fetch here.
  // Prefers the backend-fetched months lookup (TrucksStep); falls back to
  // the static list for the unauthenticated dashboard-demo path.
  const monthLabel = state.monthsLookup.length
    ? state.monthsLookup.find((m) => m.value === state.month)?.label ?? "—"
    : MONTHS.find((m) => m[0] === state.month)?.[1] ?? "—";

  // "Month the mileage limit was exceeded" (state.amendMonthMileage) - saved
  // by AmendedReturnStep alongside the reason, but distinct from "Month first
  // used" above, so it needs its own label lookup here.
  const mileageMonthLabel = state.amendMonthMileage
    ? (state.monthsLookup.length
        ? state.monthsLookup.find((m) => m.value === state.amendMonthMileage)?.label
        : MONTHS.find((m) => m[0] === state.amendMonthMileage)?.[1]) ?? state.amendMonthMileage
    : "";

  // Looks up the name for the preparer that will actually be used on this return: this
  // return's own override (state.returnTaxPreparerId, set from the Form step's dropdown -
  // see AppContext's returnTaxPreparerId comment) if one was picked, else the business's
  // linked default (state.biz.taxPreparerId). Fetched from the caller's saved-preparer list
  // rather than trusting state.preparer, since that only reflects the preparer entered in
  // *this* wizard session, which may differ from the one actually referenced.
  const effectivePreparerId = state.returnTaxPreparerId ?? state.biz.taxPreparerId;
  useEffect(() => {
    if (!getToken() || !effectivePreparerId) {
      setPreparerName("");
      return;
    }
    let cancelled = false;
    listTaxPreparerOptions()
      .then((options) => {
        if (cancelled) return;
        const match = options.find((p) => p.id === effectivePreparerId);
        setPreparerName(match ? match.name : "");
      })
      .catch(() => {
        if (!cancelled) setPreparerName("");
      });
    return () => {
      cancelled = true;
    };
  }, [effectivePreparerId]);

  // Preview-validates the return's XML before moving on to payment (no
  // payment method chosen yet, so this is a check, not the official
  // generate/submit - that still only happens after the service fee is paid,
  // via PaymentDetailsStep -> submitReturn, unchanged). If the IRS schema
  // rejects it (422) or anything else goes wrong, there's no point sending the
  // filer further into the flow for a return that can't actually be filed, so
  // this routes to /confirm's error branch instead of silently advancing.
  const handleContinue = async () => {
    if (!getToken() || !state.returnId) {
      onNext();
      return;
    }
    setValidating(true);
    try {
      await validateXml(state.returnId);
      onNext();
    } catch (err) {
      navigate("/confirm", {
        state: { error: err.message || "We couldn't validate your return. Please try again or contact support." },
      });
    } finally {
      setValidating(false);
    }
  };

  return (
    <div>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
         Review
      </div>
      <h2 className="text-2xl m-[4px_0]">{viewOnly ? "Return details" : "Review your return"}</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">
        {viewOnly
          ? "This is exactly what was sent to the IRS."
          : "This is exactly what we'll send to the IRS. You can still go back and edit anything."}
      </p>

      <div className="grid gap-3.5 sm:grid-cols-3 mb-4">
        <Panel padded className="min-w-0 overflow-hidden">
          <CardIcon icon={Building2} />
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-[var(--muted)]">Business</div>
            <EditButton onClick={onEditBusiness} />
          </div>
          <div className="font-semibold break-words">{state.biz.name || "—"}</div>
          <div className="text-xs text-[var(--muted)] mt-2">EIN</div>
          <div className="font-mono break-all">{state.biz.ein || "—"}</div>
          {state.biz.a1 && (
            <>
              <div className="text-xs text-[var(--muted)] mt-2">Address</div>
              <div className="text-sm break-words">
                {state.biz.a1}
                {state.biz.a2 && <>, {state.biz.a2}</>}
                <br />
                {[state.biz.city, state.biz.state, state.biz.zip].filter(Boolean).join(", ")}
              </div>
            </>
          )}
          {preparerName && (
            <>
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="text-xs text-[var(--muted)]">Tax preparer</div>
                <EditButton onClick={onEditPreparer} />
              </div>
              <div className="font-semibold break-words">{preparerName}</div>
            </>
          )}
        </Panel>
        <Panel padded className="min-w-0 overflow-hidden">
          <CardIcon icon={FileText} />
          <div className="text-xs text-[var(--muted)]">Filing type</div>
          <div className="font-semibold break-words">{RETURN_TYPE_LABELS[state.returnType] || "—"}</div>
          {isAmended && state.amendReason && (
            <>
              <div className="text-xs text-[var(--muted)] mt-2">Explanation</div>
              <div className="text-sm mt-1 break-words">{AMEND_REASON_LABELS[state.amendReason] || state.amendReason}</div>
            </>
          )}
          {isAmended && state.amendReason === "mileage" && mileageMonthLabel && (
            <div className="text-xs text-[var(--muted)] mt-1">Month exceeded: {mileageMonthLabel}</div>
          )}
          {state.addressChange && (
            <div className="text-xs text-[var(--muted)] mt-2">Address change flagged to the IRS</div>
          )}
        </Panel>
        <Panel padded className="min-w-0 overflow-hidden">
          <CardIcon icon={Truck} />
          <div className="text-xs text-[var(--muted)]">Month first used</div>
          <div className="font-semibold break-words">{monthLabel}</div>
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="text-xs text-[var(--muted)]">Vehicles</div>
            <EditButton
              onClick={() =>
                onGoToStep?.(
                  isVinCorrection ? "vinCorrection" : isWeightAmended ? "amendDetail" : isAmended ? "amendedReturn" : "trucks"
                )
              }
            />
          </div>
          <div className="break-words">
            {isVinCorrection ? (
              `${state.correctedVins.length} vehicle${state.correctedVins.length === 1 ? "" : "s"} corrected`
            ) : (
              // Weight-amended returns carry their vehicles in amendVehicles,
              // not the return's regular vehicle list - every TGW-increase
              // worksheet row is a taxable vehicle, none suspended - so
              // reported/taxable come from amendVehicles.length rather than
              // the backend's (always-0, for this filing type) counts.
              <>
                {isWeightAmended ? state.amendVehicles.length : t.reported} reported ·{" "}
                {isWeightAmended ? state.amendVehicles.length : t.taxable} taxable ·{" "}
                {isWeightAmended ? 0 : t.suspended} suspended
              </>
            )}
          </div>
        </Panel>
      </div>

      {isVinCorrection ? (
        <Banner tone="info" icon="ℹ️" className="mb-4">
          A VIN correction reissues your Schedule 1 for {state.correctedVins.length || 0} corrected vehicle
          {state.correctedVins.length === 1 ? "" : "s"} — <b>no tax is due</b>.
        </Banner>
      ) : isEmptyFinalReturn ? (
        <Banner tone="info" icon="ℹ️" className="mb-4">
          This is your <b>final return</b> — no vehicles to report and <b>no tax is due</b>.
        </Banner>
      ) : (
        <Panel padded className="mb-4">
          <h3 className="text-[19px] mt-0 mb-2.5">Tax summary</h3>
          <div className="flex justify-between items-baseline py-[9px] text-sm">
            <span className="text-[var(--muted)]">Tax (line 2)</span>
            <b className="font-mono font-medium">{money(t.line2)}</b>
          </div>
          {/* For a weight-increase amendment, Line 3 is shown once already, right
              under its own vehicle table above (isWeightAmended branch) - not
              repeated here. Every other return type never has a non-zero
              Line 3 (see Return2290Assembler - it's exclusively the
              weight-increase worksheet), so this row is effectively dead for
              them too; kept as a guarded fallback rather than deleted outright. */}
          {!isWeightAmended && !!t.line3 && (
            <div className="flex justify-between items-baseline py-[9px] border-b border-dashed border-[var(--line-2)] text-sm">
              <span className="text-[var(--muted)]">Additional tax from weight increase (line 3)</span>
              <b className="font-mono font-medium">{money(t.line3)}</b>
            </div>
          )}
          <div className="flex justify-between items-baseline py-[9px] border-b border-dashed border-[var(--line-2)] text-sm">
            <span className="text-[var(--muted)]">Total tax (line 4)</span>
            <b className="font-mono font-medium">{money(t.line4)}</b>
          </div>
          {!!t.line5 && (
            <div className="flex justify-between items-baseline py-[9px] border-b border-dashed border-[var(--line-2)] text-sm">
              <span className="text-[var(--muted)]">Credits (line 5)</span>
              <b className="font-mono font-medium">-{money(t.line5)}</b>
            </div>
          )}
          <div className="flex justify-between items-baseline mt-2.5 pt-3 border-t-2 border-[var(--ink)]">
            <span className="font-disp uppercase tracking-[.08em] text-[13px]">Balance due (line 6)</span>
            <b className="font-mono text-2xl font-semibold">{money(t.line6)}</b>
          </div>
        </Panel>
      )}

      {!isVinCorrection && !isEmptyFinalReturn && t.creditsExceedTax && (
        <Banner tone="danger" icon="⚠️" className="mb-4">
          Total Credits cannot exceed the Total Tax amount. Please reduce the credit amount or update your
          vehicle information.
        </Banner>
      )}

      {!isVinCorrection && credits.length > 0 && (
        <Panel padded className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <h3 className="text-[19px] m-0">Credits claimed (line 5)</h3>
            <EditButton onClick={() => onGoToStep?.("credits")} />
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {["#", "Reason", "VIN", "Date", "Amount", "Explanation"].map((h) => (
                  <th
                    key={h}
                    className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {credits.map((row, i) => (
                <tr key={row.id ?? i} className="hover:bg-[var(--accent-soft)] transition-colors">
                  <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                  <td className="text-left p-3 border-b border-[var(--line)]">
                    {creditReasons.find(([value]) => value === row.reason)?.[1] || row.reason}
                  </td>
                  <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{row.vin}</td>
                  <td className="text-left p-3 border-b border-[var(--line)]">{row.date}</td>
                  <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">
                    {money(Number(row.amount) || 0)}
                  </td>
                  <td className="text-left p-3 border-b border-[var(--line)]">{row.explanation || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {isVinCorrection ? (
        <Panel padded className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <h3 className="text-[19px] m-0">VIN correction</h3>
            <EditButton onClick={() => onGoToStep?.("vinCorrection")} />
          </div>
          <div className="text-xs text-[var(--muted)] mb-1">Explanation</div>
          <p className="text-sm mb-2.5 break-words">{state.vinCorrectionExpl || "—"}</p>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {["#", "Old VIN", "Corrected VIN", "Category", "Type"].map((h) => (
                  <th
                    key={h}
                    className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.correctedVins.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-[var(--muted)] p-[22px_12px]">
                    No corrected vehicles.
                  </td>
                </tr>
              ) : (
                state.correctedVins.map((r, i) => (
                  <tr key={r.id ?? i} className="hover:bg-[var(--accent-soft)] transition-colors">
                    <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                    <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{r.oldVin}</td>
                    <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{r.vin}</td>
                    <td className="text-left p-3 border-b border-[var(--line)]">
                      {r.suspended ? "W" : r.cat}
                      {" · "}
                      {r.suspended ? "suspended" : weightLabelFor(r.cat, state.weightCategoriesLookup)}
                    </td>
                    <td className="text-left p-3 border-b border-[var(--line)]">
                      {[r.suspended && "Suspended", r.logging && "Logging"].filter(Boolean).join(", ") || "Standard"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Panel>
      ) : isWeightAmended ? (
        <Panel padded className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <h3 className="text-[19px] m-0">Vehicles whose weight increased</h3>
            <EditButton onClick={() => onGoToStep?.("amendDetail")} />
          </div>
          <div className="text-xs text-[var(--muted)] mb-1">Explanation</div>
          <p className="text-sm mb-2.5 break-words">
            {AMEND_REASON_LABELS[state.amendReason] || "—"}
          </p>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {["#", "VIN", "Previous category", "New category", "Type"].map((h) => (
                  <th
                    key={h}
                    className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.amendVehicles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-[var(--muted)] p-[22px_12px]">
                    No affected vehicles.
                  </td>
                </tr>
              ) : (
                state.amendVehicles.map((r, i) => (
                  <tr key={r.id ?? i} className="hover:bg-[var(--accent-soft)] transition-colors">
                    <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                    <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{r.vin}</td>
                    <td className="text-left p-3 border-b border-[var(--line)]">
                      {r.oldCat}
                      {" · "}
                      {weightLabelFor(r.oldCat, state.weightCategoriesLookup)}
                    </td>
                    <td className="text-left p-3 border-b border-[var(--line)]">
                      {r.newCat}
                      {" · "}
                      {weightLabelFor(r.newCat, state.weightCategoriesLookup)}
                    </td>
                    <td className="text-left p-3 border-b border-[var(--line)]">{r.logging ? "Logging" : "Standard"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {/* Called out here, next to the vehicles it's for - same treatment as
              the mileage-exceeded Line 2 total below. Read straight from the
              backend-computed total (t.line3, set by
              TaxReturnServiceImpl.updateFormType on this step's own Continue)
              rather than re-deriving client-side, so it always matches what's
              actually stored/filed. No longer duplicated in the Tax summary
              panel further down - see the isWeightAmended exclusion there. */}
          <div className="flex justify-between items-baseline mt-3.5 pt-3 border-t-2 border-[var(--ink)]">
            <span className="font-disp uppercase tracking-[.08em] text-[13px]">Additional tax (line 3)</span>
            <b className="font-mono text-2xl font-semibold">{money(t.line3)}</b>
          </div>
        </Panel>
      ) : isEmptyFinalReturn ? (
        <Panel padded className="mb-4">
          <h3 className="text-[19px] mt-0 mb-2.5">Vehicles</h3>
          <p className="text-[var(--muted)] text-sm">Final return — no vehicles to report.</p>
        </Panel>
      ) : (
        <Panel padded className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <h3 className="text-[19px] m-0">Trucks on this return</h3>
            {/* Mileage-exceeded amended returns collect their vehicles right on
                the Amended Return page itself (there's no separate "trucks"
                step for amended/VIN-correction filers - see stepConfig.js). */}
            <EditButton onClick={() => onGoToStep?.(isAmended ? "amendedReturn" : "trucks")} />
          </div>
          {isAmended && state.amendReason && (
            <>
              <div className="text-xs text-[var(--muted)] mb-1">Explanation</div>
              <p className="text-sm mb-2.5 break-words">{AMEND_REASON_LABELS[state.amendReason] || state.amendReason}</p>
            </>
          )}
          {/* This branch also covers a Final return that DOES list vehicles, plus
              mileage-exceeded amended returns (isWeightAmended/isVinCorrection/
              isEmptyFinalReturn are handled above) - those vehicles live in the
              isolated state.mileageVehicles, not state.trucks (Original Return). */}
          <TrucksTable
            trucks={isAmended ? state.mileageVehicles : state.trucks}
            month={state.month}
            weightCategories={state.weightCategoriesLookup}
          />
          {/* i2290 "Suspended vehicles exceeding the mileage use limit": "Report
              the tax on Form 2290, line 2" - NOT line 3 (that's exclusively the
              taxable-gross-weight-increase worksheet - see the isWeightAmended
              branch above). These vehicles are ordinary taxable vehicles, so
              this total is exactly what the Tax summary panel below shows as
              Line 2 - called out here too, next to the vehicles it's for. */}
          {isMileageAmended && (
            <div className="flex justify-between items-baseline mt-3.5 pt-3 border-t-2 border-[var(--ink)]">
              <span className="font-disp uppercase tracking-[.08em] text-[13px]">Tax on these vehicles (line 2)</span>
              <b className="font-mono text-2xl font-semibold">
                {money(
                  round2(
                    state.mileageVehicles.reduce(
                      (sum, t) => sum + (t.taxAmt != null ? Number(t.taxAmt) : truckTax(t, state.month)),
                      0
                    )
                  )
                )}
              </b>
            </div>
          )}
        </Panel>
      )}

      {showsPriorYear && hasPriorYearData && (
        <Panel padded className="mb-4">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <h3 className="text-[19px] m-0">Prior-year suspended vehicles</h3>
            <EditButton onClick={() => onGoToStep?.("priorYear")} />
          </div>

          {priorYear.line8aChecked && (
            <div className="mb-3.5">
              <div className="text-sm mb-1.5">
                Last year's suspended vehicles stayed under the mileage limit
                {priorYear.line8bVins.length > 0 && ", except:"}
              </div>
              {priorYear.line8bVins.length > 0 && (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      {["#", "VIN"].map((h) => (
                        <th
                          key={h}
                          className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {priorYear.line8bVins.map((vin, i) => (
                      <tr key={vin + i} className="hover:bg-[var(--accent-soft)] transition-colors">
                        <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                        <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{vin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {priorYear.line9Checked && (
            <div>
              <div className="text-sm mb-1.5">Suspended vehicles sold or transferred:</div>
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {["#", "VIN", "Buyer", "Date"].map((h) => (
                      <th
                        key={h}
                        className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {priorYear.line9Transfers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-[var(--muted)] p-[16px_12px] text-sm">
                        None.
                      </td>
                    </tr>
                  ) : (
                    priorYear.line9Transfers.map((t, i) => (
                      <tr key={t.id ?? i} className="hover:bg-[var(--accent-soft)] transition-colors">
                        <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                        <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{t.vin}</td>
                        <td className="text-left p-3 border-b border-[var(--line)]">{t.buyerName}</td>
                        <td className="text-left p-3 border-b border-[var(--line)]">{t.transferDate}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      <div className="flex justify-between gap-3 mt-6">
        {viewOnly ? (
          <Button variant="ghost" onClick={onBack}>
            ← Back to My Filings
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onBack}>
              ← back
            </Button>
            <Button onClick={handleContinue} disabled={validating || t.creditsExceedTax}>
              {validating ? "Validating your return…" : "Continue to payment →"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ReviewStep;
